import { recipe, splitRecipeProps, type RecipeVariantProps } from "dx-styles"
import type { ReactElement } from "react"

import { tokens } from "../theme"
import type { ElementProps } from "../types"

export const stackRecipe = recipe({
  base: {
    display: "flex",
  },
  variants: {
    direction: {
      column: { flexDirection: "column" },
      row: { flexDirection: "row" },
    },
    gap: {
      none: { gap: tokens.space.none },
      xs: { gap: tokens.space.xs },
      sm: { gap: tokens.space.sm },
      md: { gap: tokens.space.md },
      lg: { gap: tokens.space.lg },
    },
    /** Cross-axis — `align-items`. */
    align: {
      start: { alignItems: "flex-start" },
      center: { alignItems: "center" },
      end: { alignItems: "flex-end" },
      stretch: { alignItems: "stretch" },
    },
    /** Main-axis — `justify-content`. `between`, not `space-between`. */
    justify: {
      start: { justifyContent: "flex-start" },
      center: { justifyContent: "center" },
      end: { justifyContent: "flex-end" },
      between: { justifyContent: "space-between" },
    },
    wrap: {
      nowrap: { flexWrap: "nowrap" },
      wrap: { flexWrap: "wrap" },
    },
  },
  defaultVariants: {
    direction: "column",
    // A Stack exists to space things, so it spaces them by default. Opt out
    // with `gap="none"`.
    gap: "md",
    align: "start",
    justify: "start",
    wrap: "nowrap",
  },
})

type StackVariantProps = RecipeVariantProps<typeof stackRecipe>

export type StackProps = StackVariantProps &
  Omit<ElementProps<"div">, keyof StackVariantProps>

const Stack = (props: StackProps): ReactElement => {
  const { otherProps, variantProps } = splitRecipeProps(stackRecipe, props)

  return (
    <div
      {...otherProps}
      className={stackRecipe(variantProps)}
    />
  )
}

/** The direction is what the wrapper exists to fix, so it is not selectable. */
export type HStackProps = Omit<StackProps, "direction">
export type VStackProps = Omit<StackProps, "direction">

/**
 * A row. Defaults to `align="center"` — items sitting side by side almost always
 * want their centres lined up, and a row of mixed-height content (icon, label,
 * button) looks wrong stretched.
 */
export const HStack = ({
  align = "center",
  ...props
}: HStackProps): ReactElement => {
  return (
    <Stack
      {...props}
      align={align}
      direction="row"
    />
  )
}

/**
 * A column. Defaults to `align="stretch"` so children fill the width — the
 * recipe's `start` default makes every child shrink to its own content, which
 * collapses cards and rows to half-width. Mirrors HStack's `center` override.
 */
export const VStack = ({
  align = "stretch",
  ...props
}: VStackProps): ReactElement => {
  return (
    <Stack
      {...props}
      align={align}
      direction="column"
    />
  )
}
